import { getProductThumbnail } from '@repo/types'

import type { OutreachCertificateView } from 'services/demosApi'

import {
  type Box,
  OUTREACH_PNG,
  fitContain,
  mockCertificateNumber,
  outreachPngFilename,
  photoArea,
  readableAccent,
} from './outreachCertificateLayout'

/**
 * VRG-723 — composes the OUTREACH CERTIFICATE as a PNG, in the browser, on a canvas.
 *
 * Why client-side: the image is a picture of a certificate the platform already has every input
 * for (the demo's logo, the outreach product's photo and title, the brand's accent and fonts), so
 * a server renderer would be a second copy of the certificate's look to keep in step, plus a
 * headless-browser or `sharp` dependency the API image does not carry (it is a root devDependency
 * only, absent from the `--filter api` Docker image).
 *
 * Every image is fetched as a BLOB through the API (`fetchImage`), never drawn from its own URL:
 * a cross-origin image drawn without CORS headers TAINTS the canvas and `toBlob` then throws, and
 * most brand CDNs send no CORS headers. The geometry and the house rules live in
 * `outreachCertificateLayout.ts`, which the guard reads.
 */

type Drawable = ImageBitmap | HTMLImageElement

export interface RenderOutreachInput {
  view: OutreachCertificateView
  /** Bytes of an image the demo owns (its logo or a product photo). */
  fetchImage: (src: string) => Promise<Blob>
}

const toDrawable = async (blob: Blob): Promise<Drawable> => {
  if (typeof createImageBitmap === 'function') {
    try {
      return await createImageBitmap(blob)
    } catch {
      /* SVG and some formats are not bitmaps; fall through to <img>. */
    }
  }
  const url = URL.createObjectURL(blob)
  try {
    const img = new Image()
    img.decoding = 'async'
    img.src = url
    await img.decode()
    return img
  } finally {
    // Released after decode; the decoded image stays drawable.
    setTimeout(() => URL.revokeObjectURL(url), 0)
  }
}

const sizeOf = (d: Drawable) =>
  'naturalWidth' in d ? { w: d.naturalWidth, h: d.naturalHeight } : { w: d.width, h: d.height }

/** A missing image is not fatal: the card renders without it rather than not at all. */
const tryLoad = async (src: string | null | undefined, fetchImage: RenderOutreachInput['fetchImage']) => {
  if (!src) return null
  try {
    return await toDrawable(await fetchImage(src))
  } catch {
    return null
  }
}

const roundRect = (ctx: CanvasRenderingContext2D, b: Box, r: number) => {
  ctx.beginPath()
  ctx.moveTo(b.x + r, b.y)
  ctx.lineTo(b.x + b.w - r, b.y)
  ctx.quadraticCurveTo(b.x + b.w, b.y, b.x + b.w, b.y + r)
  ctx.lineTo(b.x + b.w, b.y + b.h - r)
  ctx.quadraticCurveTo(b.x + b.w, b.y + b.h, b.x + b.w - r, b.y + b.h)
  ctx.lineTo(b.x + r, b.y + b.h)
  ctx.quadraticCurveTo(b.x, b.y + b.h, b.x, b.y + b.h - r)
  ctx.lineTo(b.x, b.y + r)
  ctx.quadraticCurveTo(b.x, b.y, b.x + r, b.y)
  ctx.closePath()
}

/** Greedy word wrap, ellipsised on the last allowed line. */
const wrap = (ctx: CanvasRenderingContext2D, text: string, maxWidth: number, maxLines: number): string[] => {
  const words = text.trim().split(/\s+/)
  const lines: string[] = []
  let line = ''
  for (const word of words) {
    const next = line ? `${line} ${word}` : word
    if (ctx.measureText(next).width <= maxWidth || !line) {
      line = next
      continue
    }
    lines.push(line)
    line = word
    if (lines.length === maxLines) break
  }
  if (lines.length < maxLines && line) lines.push(line)
  const consumed = lines.join(' ').split(/\s+/).length
  if (consumed < words.length && lines.length) {
    let last = lines[lines.length - 1]
    while (last.length > 1 && ctx.measureText(`${last}…`).width > maxWidth) last = last.slice(0, -1)
    lines[lines.length - 1] = `${last.trimEnd()}…`
  }
  return lines
}

const fontStack = (family: string | null | undefined) =>
  `${family ? `"${family.replace(/"/g, '')}", ` : ''}Inter, "Helvetica Neue", Arial, sans-serif`

/** Best effort: the brand font, if the document can load it in time; the stack falls back otherwise. */
const loadFont = async (spec: string) => {
  try {
    await Promise.race([document.fonts.load(spec), new Promise((r) => setTimeout(r, 1500))])
  } catch {
    /* fall back to the stack */
  }
}

/** A rounded label. `x` is its centre (`anchor: 'centre'`) or its left edge (`anchor: 'left'`). */
const pill = (
  ctx: CanvasRenderingContext2D,
  text: string,
  x: number,
  y: number,
  { font, bg, ink, padX, h, anchor = 'centre' }: { font: string; bg: string; ink: string; padX: number; h: number; anchor?: 'centre' | 'left' },
) => {
  ctx.font = font
  const w = ctx.measureText(text).width + padX * 2
  const left = anchor === 'centre' ? x - w / 2 : x
  roundRect(ctx, { x: left, y, w, h }, h / 2)
  ctx.fillStyle = bg
  ctx.fill()
  ctx.fillStyle = ink
  ctx.textAlign = 'center'
  ctx.textBaseline = 'middle'
  ctx.fillText(text, left + w / 2, y + h / 2 + 1)
}

/** Draws the certificate onto a fresh canvas and returns it. Throws if there is no certificate. */
export const drawOutreachCertificate = async ({ view, fetchImage }: RenderOutreachInput): Promise<HTMLCanvasElement> => {
  const cert = view.certificate
  if (!cert) throw new Error('This demo has no outreach certificate yet.')

  const { width, height, colors, card, header, tile } = OUTREACH_PNG
  const canvas = document.createElement('canvas')
  canvas.width = width
  canvas.height = height
  const ctx = canvas.getContext('2d')
  if (!ctx) throw new Error('This browser cannot draw the image.')

  const heading = fontStack(view.theme.fontHeading)
  const body = fontStack(view.theme.fontBody)
  const accent = readableAccent(view.theme.primaryColor)
  await Promise.all([loadFont(`700 52px ${heading}`), loadFont(`400 30px ${body}`)])
  /*
   * VRG-777 — `getProductThumbnail`, NOT `cert.images[0]`.
   *
   * This is the one thumbnail consumer that does not degrade. `fetchImage` fetches the
   * URL and decodes it as an image, so a video URL here throws — and while `tryLoad`
   * swallows that into a logo-less, photo-less card rather than crashing, the outcome is
   * an outreach PNG silently missing the product shot that is the entire point of it.
   * Every other consumer renders a placeholder a human can see and report; this one
   * produces a plausible-looking file that is quietly wrong.
   */
  const [logo, photo] = await Promise.all([
    tryLoad(view.logo, fetchImage),
    tryLoad(getProductThumbnail(cert.images), fetchImage),
  ])

  // Page and card. LIGHT, always: nothing here reads the viewer's colour scheme.
  ctx.fillStyle = colors.page
  ctx.fillRect(0, 0, width, height)
  roundRect(ctx, card, card.radius)
  ctx.fillStyle = colors.card
  ctx.fill()
  ctx.strokeStyle = colors.border
  ctx.lineWidth = 2
  ctx.stroke()

  // Header: the brand's own logo, else its name.
  const headerBox: Box = { x: card.x, y: card.y, w: card.w, h: header.h }
  if (logo) {
    const s = sizeOf(logo)
    const fit = fitContain(s.w, s.h, { x: headerBox.x + 260, y: headerBox.y + 32, w: headerBox.w - 520, h: headerBox.h - 64 })
    ctx.drawImage(logo, fit.x, fit.y, fit.w, fit.h)
  } else {
    ctx.font = `700 40px ${heading}`
    ctx.fillStyle = colors.ink
    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'
    ctx.fillText(wrap(ctx, view.companyName, headerBox.w - 160, 1)[0] ?? '', headerBox.x + headerBox.w / 2, headerBox.y + headerBox.h / 2)
  }
  ctx.fillStyle = colors.border
  ctx.fillRect(headerBox.x, headerBox.y + headerBox.h, headerBox.w, 2)

  // Photo tile: the photo whole, padded, centred. Never cropped, never zoomed past the padding.
  roundRect(ctx, tile, tile.radius)
  ctx.fillStyle = colors.tile
  ctx.fill()
  if (photo) {
    const s = sizeOf(photo)
    const fit = fitContain(s.w, s.h, photoArea())
    ctx.drawImage(photo, fit.x, fit.y, fit.w, fit.h)
  } else {
    ctx.font = `400 28px ${body}`
    ctx.fillStyle = colors.faint
    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'
    ctx.fillText('No product photo', tile.x + tile.w / 2, tile.y + tile.h / 2)
  }

  // The label, ON the pixels, inside the tile's top padding (so it never covers the photo).
  pill(ctx, OUTREACH_PNG.label, width / 2, tile.y + 22, {
    font: `600 24px ${body}`,
    bg: colors.label,
    ink: colors.labelInk,
    padX: 22,
    h: 44,
  })

  // Text block.
  const left = tile.x
  const textWidth = tile.w
  let y = tile.y + tile.h + 44
  pill(ctx, 'Verified by Vera', left, y, { font: `600 24px ${body}`, bg: accent, ink: '#FFFFFF', padX: 22, h: 44, anchor: 'left' })

  y += 44 + 34
  ctx.font = `700 50px ${heading}`
  ctx.fillStyle = colors.ink
  ctx.textAlign = 'left'
  ctx.textBaseline = 'top'
  for (const line of wrap(ctx, cert.productTitle, textWidth, 2)) {
    ctx.fillText(line, left, y)
    y += 60
  }
  y += 6
  ctx.font = `400 30px ${body}`
  ctx.fillStyle = colors.muted
  ctx.fillText(wrap(ctx, view.companyName, textWidth, 1)[0] ?? '', left, y)

  // Facts, pinned to the card's foot.
  // A two-line title + the brand line ends ~y 1100; the divider sits below it, the facts below that.
  const factsY = card.y + card.h - OUTREACH_PNG.factsFromBottom
  ctx.fillStyle = colors.border
  ctx.fillRect(left, factsY - 22, textWidth, 2)
  const fact = (label: string, value: string, x: number) => {
    ctx.font = `400 22px ${body}`
    ctx.fillStyle = colors.faint
    ctx.fillText(label, x, factsY)
    ctx.font = `600 28px ${body}`
    ctx.fillStyle = colors.ink
    ctx.fillText(value, x, factsY + 32)
  }
  fact('Certificate', mockCertificateNumber(cert.id), left)
  if (cert.sku) fact('SKU', wrap(ctx, cert.sku, textWidth / 2 - 20, 1)[0] ?? '', left + textWidth / 2)

  return canvas
}

/** Renders and saves `<subdomain>-outreach-certificate.png`. */
export const downloadOutreachPng = async (input: RenderOutreachInput): Promise<void> => {
  const canvas = await drawOutreachCertificate(input)
  const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/png'))
  if (!blob) throw new Error('The image could not be created.')
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = outreachPngFilename(input.view.subdomain)
  document.body.appendChild(a)
  a.click()
  a.remove()
  setTimeout(() => URL.revokeObjectURL(url), 1000)
}
